import { useMutation } from '@northladder/services';
import { IApiError } from '@northladder/utilities';

import { BIDS_API } from '../bidsAPIClient';
import { normalizeBidTag } from '../transformers';
import { IUpdatedBidDetailsData } from '../types';

import { useBidDetailsQueryInvalidation } from './useBidDetailsQuery';
import { useBidsListingQueryInvalidation } from './useSearchBidsQuery';

/**
 * This is to track the create or update bid status mutation keys in react query cache.
 */
const MUT_CREATE_BID_STATUS = 'update-bid-status-mutation-key';

interface IUpdateBidMutationParam {
  assetId: string;
  onError?: (error: IApiError) => void;
  onSuccess: (updatedBidsData: IUpdatedBidDetailsData) => void;
}

/**
 * React Query mutation function for managing create or update bid api
 */
export const useUpdateBidMutation = ({
  assetId,
  onSuccess,
  onError,
}: IUpdateBidMutationParam) => {
  const { invalidateBidDetails } = useBidDetailsQueryInvalidation(assetId);
  const { invalidateBidsListing } = useBidsListingQueryInvalidation();

  const { isPending, isError, error, data, mutate, mutateAsync } = useMutation({
    mutationKey: [MUT_CREATE_BID_STATUS],
    networkMode: 'always',
    mutationFn: BIDS_API.updateBidRequest,
    onSuccess: (response) => {
      const updatedData = response?.data?.data || {};
      const {
        _id: bidId = '',
        autoTopupEnabled,
        availableQuantity = 0,
        isBidActive,
        isSpotSummit,
        maxQuantity = 0,
        rank,
        spotPrice = 0,
        tag,
        topSpotPrice,
        updatedDate,
      } = updatedData;

      // normalizeBidTag always returns a valid TBidTag (defaults to 'no-bids')
      const normalizedTag = normalizeBidTag(tag);

      // TODO: Sowed do this in the transformer and pass it down as callback
      const updatedBidsData = {
        topBidAmount: topSpotPrice || 0, // Highest Bid,
        myBidUpdates: {
          availableQuantity,
          bidId,
          isAutoTopupEnabled: autoTopupEnabled, // Quantity
          isBidActive, // Bid Active
          isSpotSummit, // Spot Summit status badge
          maxQuantity, // Quantity
          rank: normalizedTag, // Backend-driven bid rank tag
          rankNumber: rank || 0, // Rank for status badge
          spotPrice, // My Bid
          updatedDate: updatedDate || new Date().getTime(),
        },
      };

      // Call onSuccess first to update cache optimistically
      // This ensures the UI updates immediately before invalidation triggers refetch
      onSuccess(updatedBidsData);

      // Use queueMicrotask to ensure cache update is applied before invalidation
      // This prevents the refetch from overwriting the optimistic update
      queueMicrotask(() => {
        invalidateBidDetails();
        invalidateBidsListing();
      });
    },
    onError,
  });

  return {
    data,
    error,
    isError,
    isPending,
    mutate,
    mutateAsync,
  };
};
